import _asyncToGenerator from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import _regeneratorRuntime from "/app/node_modules/next/dist/compiled/@babel/runtime/regenerator/index.js";
import * as React from 'react';
import { initISODate } from '~logic/helpers/initISODate';
import { wait } from '~logic/helpers/wait';
import { useIsMountedRef } from '~logic/hooks/useIsMountedRef';
import { useStorage } from '../../../contexts/StorageContext/StorageProvider';
import { usePersistenceScope } from './usePersistenceScope';

/**
 * Creates a persisted useState that is synced into the storage
 * on each update under the provided `key`.
 * Initially the value is loaded from the persistence by useEffect.
 *
 * * When in "not-loaded" state, the value is set to undefined,
 * * when in "loaded-empty" state, the value is set to null,
 * * otherwise, the type is defined by the usage.
 * (the "isLoaded" boolean was not used by choice to keep the return type a simple tuple)
 *
 * @returns tuple of `[value, setValue, clearValue]` handlers
 */
export var usePersistedValue = function usePersistedValue(_ref) {
  var key = _ref.key;
  var refIsMounted = useIsMountedRef();
  var storage = useStorage();
  var refKey = React.useRef(key);
  var scopeKey = usePersistenceScope();

  var _React$useState = React.useState(undefined),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      value = _React$useState2[0],
      setValue = _React$useState2[1]; // initial storage query & setting the state to retrieved value
  // (or null if nothing was stored before)


  React.useEffect(function () {
    var initFromStorage = /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var storedValue, restoredValue;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.next = 2;
                return wait(0);

              case 2:
                _context.next = 4;
                return storage.getItem(scopeKey(refKey.current));

              case 4:
                storedValue = _context.sent;
                restoredValue = storedValue ? JSON.parse(storedValue, initISODate) : null;

                if (refIsMounted.current) {
                  // Restore the value if it was not updated in the mean time.
                  // Since `undefined` means uninitialized,
                  // we can assume that if the prevValue !== undefined,
                  // the value was updated before this init effect finished its task,
                  // and we should therefore keep the prevValue
                  setValue(function (prevValue) {
                    var isStillNotSet = prevValue === undefined;
                    return isStillNotSet ? restoredValue : prevValue;
                  });
                }

              case 7:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function initFromStorage() {
        return _ref2.apply(this, arguments);
      };
    }();

    initFromStorage();
  }, [refIsMounted, scopeKey, storage]); // set the local state & update the persistence

  var setAndStoreData = React.useCallback(function (updatedData) {
    var stringifiedData = JSON.stringify(updatedData);
    storage.setItem(scopeKey(refKey.current), stringifiedData);
    setValue(updatedData);
  }, [scopeKey, storage]); // set local state to null & remove the item from persistence

  var clearValue = React.useCallback(function () {
    storage.removeItem(scopeKey(refKey.current));
    setValue(null);
  }, [scopeKey, storage]);
  return [value, setAndStoreData, clearValue];
};